// Modules
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import * as auth0 from 'auth0-js';
import * as JWT from 'jwt-decode';
import { Account, Configuration } from 'msal';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { NotifierService } from '../../../shared/services/notifier.service';
import { AssetService } from '../../asset/service/asset.service';
import { ProductService } from '../../product/service/product.service';
// Models
import { Groups } from '../model/groups.model';

export const REDIRECT_PAGE_NOTIFIER_ID = 'redirectPage';
export const USER_PROFILE_SET_NOTIFIER_ID = 'userProfileSet';
export const INVALID_GROUP_ACCESS = 'Invalid Group Access';
export const GROUP_METADATA = 'https://vixxo.com/groups';
export const USER_EMAIL = 'https://vixxo.com/email';

@Injectable()
export class AuthService {
  private loggedIn: boolean;
  private loggedIn$ = new BehaviorSubject(this.loggedIn);
  refreshSubscription: any;
  private Auth0Config: any;
  private Auth0: any;
  private profile: any;
  private account: Account;
  private config: Configuration;
  private getTokenDatafromStorage: any;

  constructor(
    private notifierService: NotifierService,
    private http: HttpClient,
    private msalService: MsalService
  ) {
    let isAuthenticated = this.isAuthenticated();
    if (isAuthenticated) {
      this.setLoggedIn(true);
    }
  }

  /**
   * Send user to Auth0 Login.
   */
  public login() {
    //change to b2cLogin
    this.msalService.loginRedirect();
  }

  /**
   * Log Out Current User in session.
   */
  public logout() {
    // Remove tokens and profile and update login status subject
    localStorage.removeItem('token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('userInfo');
    // Remove the search preferences as well.
    localStorage.removeItem(ProductService.PRODUCT_SEARCH_KEY);
    localStorage.removeItem(ProductService.PRODUCT_SEARCH_GRID_KEY);
    localStorage.removeItem(AssetService.ASSET_SEARCH_KEY);
    localStorage.removeItem(AssetService.ASSET_SEARCH_GRID_KEY);
    this.setLoggedIn(false);
    this.msalService.logout();
  }

  /**
   * Handle Authentication
   */
  public handleAuth(): void {
    this.config = this.msalService.getCurrentConfiguration();
    this.account = this.msalService.getAccount();
    let authority = this.config.auth.authority;
    let clientId = this.config.auth.clientId;
    let homeAccountIdentifier = this.account.homeAccountIdentifier;
    let configData = JSON.stringify({
      authority: authority,
      clientId: clientId,
      homeAccountIdentifier: homeAccountIdentifier
    });
    this.getTokenDatafromStorage = JSON.parse(localStorage.getItem(configData));
    if (this.getTokenDatafromStorage) {
      this.setProfile(this.getTokenDatafromStorage);
    }
  }

  /**
   * Set the User's Sessionj
   * @param authResult
   */
  private setSession(getTokenDatafromStorage: any) {
    // Save session data and update login status subject
    const expiresAt = JSON.stringify(
      new Date(getTokenDatafromStorage.expiresIn * 1000).getTime
    );
    localStorage.setItem('token', getTokenDatafromStorage.accessToken);
    localStorage.setItem('id_token', getTokenDatafromStorage.idToken);
    localStorage.setItem('expires_at', expiresAt);
    this.setProfile(getTokenDatafromStorage);
  }

  /**
   * Fetch the User's Profile and Store It in a Cookie
   * @param authResult
   */
  private setProfile(authResult: any) {
    //set b2c account first
    this.setProfileWithGroupsMetadata(authResult);
    this.setLoggedIn(true);
    let redirectPage = this.getUserHomePage();
    this.notifierService.broadcast({
      name: USER_PROFILE_SET_NOTIFIER_ID
    });
    this.notifierService.broadcast({
      name: REDIRECT_PAGE_NOTIFIER_ID,
      page: redirectPage
    });
    // Use access token to retrieve user's profile
  }

  /**
   * Set the Group Metadata and save the information into the Profile
   * @param authResult
   * @param profile
   */
  public setProfileWithGroupsMetadata(authResult?: any): void {
    try {
      let jwtData;
      if (authResult !== undefined) {
        jwtData = JWT(authResult.accessToken);
        this.profile = jwtData;
        this.setUserInfo(this.profile);
      } else {
        jwtData = JWT(this.getAccessToken());
        this.profile = jwtData;
      }
      this.profile[GROUP_METADATA] = jwtData[GROUP_METADATA];
    } catch (Error) {
      console.log(Error.Message);
    }
  }

  /**
   * Validates if the current user still authenticated.
   * @returns {boolean}
   */
  public isAuthenticated(): boolean {
    // Check whether the current time is past the token's expiry time
    //check account is present in storage
    let getExpDatefromTokenStorage = this.getExpDate();
    if (
      typeof getExpDatefromTokenStorage !== 'undefined' &&
      getExpDatefromTokenStorage !== null &&
      getExpDatefromTokenStorage != ''
    ) {
      const expdate1 = new Date(0);
      let expDate = expdate1.setUTCSeconds(Number(getExpDatefromTokenStorage));
      const date = new Date().getTime();
      if (date > expDate) {
        return false;
      }
      return true;
    }
    return false;
  }

  /**
   * Set User as Logged In
   * @param value
   */
  private setLoggedIn(value: boolean) {
    // Update login status subject
    this.loggedIn$.next(value);
    this.loggedIn = value;
  }

  // /**
  //  * Get Auth0 Config Properties from server and init Auth0 from Proxy Server
  //  * @returns {Observable<any>}
  //  */
  public getConfigAndInitAuth0(): Observable<any> {
    if (this.Auth0 && this.Auth0Config) {
      return Observable.of(this.Auth0);
    } else {
      return this.http
        .get(RestApiModel.PROXY_CONFIG_URL)
        .map((res: any) => {
          let auth0Config = res;
          this.Auth0Config = auth0Config;
          this.Auth0 = new auth0.WebAuth({
            redirectUri: this.Auth0Config.AUTH0_REDIRECT,
            AUTHORITY: this.Auth0Config.AUTHORITY,
            CLIENT_ID: this.Auth0Config.CLIENT_ID,
            KNOWNAUTHORITIES: this.Auth0Config.KNOWNAUTHORITIES,
            CACHELOCATION: this.Auth0Config.CACHELOCATION,
            OPENIDSCOPES: this.Auth0Config.OPENIDSCOPES,
            OFFLINE_ACCESS: this.Auth0Config.OFFLINE_ACCESS,
            UNPROTECTEDRESOURCES: this.Auth0Config.UNPROTECTEDRESOURCES
          });
        })
        .catch((error: any) => {
          return error;
        });
    }
  }

  public getEnviromentKey(): any {
    return this.Auth0;
  }
  /**
   * Gets the User Profile data.
   * @param
   */
  public getLocalStorageProfile(): any {
    if (!this.profile || this.profile === 'undefined') {
      return null;
    }
    return this.profile;
  }

  private updateSession(authResult: any) {
    const expiresAt = JSON.stringify(authResult.expiresIn * 5000 + Date.now());
    // Save auth token & expiry
    localStorage.setItem('authResult', authResult);
    localStorage.setItem('accessToken', authResult.accessToken);
    localStorage.setItem('expires_at', expiresAt);
  }

  private scheduleTokenRenewal() {
    if (!this.isAuthenticated()) {
      return;
    }
    this.unscheduleTokenRenewal();
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    const now = Date.now();
    const duration = Math.max(1, expiresAt - now);
    this.refreshSubscription = Observable.timer(duration).subscribe(() => {
      this.renewToken();
    });
  }

  public renewToken() {
    // const { AUTH0_AUDIENCE, AUTH0_RENEW_URI, AUTH0_APP_ORIGIN } = environment;
    const renewOptions = {
      audience: this.Auth0Config.AUTH0_AUDIENCE,
      redirectUri: this.Auth0Config.AUTH0_REDIRECT,
      usePostMessage: true,
      postMessageOrigin: 'localhost'
    };
    this.Auth0.renewAuth(renewOptions, (err, result) => {
      if (err) {
        return console.error(err);
      }
      this.updateSession(result);
      this.scheduleTokenRenewal();
    });
  }

  private unscheduleTokenRenewal() {
    if (!this.refreshSubscription) {
      return;
    }
    this.refreshSubscription.unsubscribe();
  }

  /**
   * Checks that current user logged in
   * has the Admin Portal Group + any of the current supported Groups.
   * @returns {boolean}
   */
  public isValidUser(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    const profile = this.getLocalStorageProfile();
    if (!profile || !profile[GROUP_METADATA]) {
      return false;
    }
    const groups = profile[GROUP_METADATA];
    return (
      groups.find(group => {
        return (
          group === Groups.ADMIN_PORTAL ||
          Groups.SPM_USER_MANAGER ||
          Groups.CUSTOMER_USER_MANAGER
        );
      }) && groups.find(group => this.isAnyValidGroup(group))
    );
  }

  /**
   * Check if current user has Admin group.
   * @returns {boolean}
   */
  public isAdmin(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => group === Groups.ASSET_ADMINISTRATOR);
    } else {
      return false;
    }
  }

  /**
   * Check if current user has Viewer group.
   * @returns {boolean}
   */
  public isViewer(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => group === Groups.ASSET_VIEWER);
    } else {
      return false;
    }
  }

  /**
   * Check if current user in session contains a specific group.
   * @param group
   * @returns {boolean}
   */
  public userHasGroup(group): boolean {
    let groups = this.getUserGroups();
    return groups.find(r => r === group);
  }

  /**
   * Check if current user has Admin or Viewer group.
   * @returns {boolean}
   */
  public isAdminOrViewer(): boolean {
    return this.isAdmin() || this.isViewer();
  }

  /**
   * Check if current user has Admin or Viewer group.
   * @returns {boolean}
   */
  public isViewerOrManager(): boolean {
    return this.isViewer() || this.isAssetManager();
  }

  /**
   * Check if current user has Asset Manager group.
   * @returns {boolean}
   */
  public isAssetManager(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => group === Groups.ASSET_MANAGER);
    } else {
      return false;
    }
  }

  /**
   * Check if current user has Users Global Admin group or Clients Admin.
   * @returns {boolean}
   */
  public isUsersAdmin(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return group === Groups.USER_ADMIN || group === Groups.CLIENT_ADMIN;
      });
    } else {
      return false;
    }
  }

  public isSPMUserManager(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return (
          group === Groups.SPM_USER_MANAGER &&
          group !== Groups.CLIENT_ADMIN &&
          group !== Groups.ASSET_MANAGER &&
          group !== Groups.ASSET_VIEWER &&
          group !== Groups.CLIENT_ADMIN &&
          group !== Groups.PRODUCT_ADMIN
        );
      });
    } else {
      return false;
    }
  }

  public isCustomerUserManager(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return (
          group === Groups.CUSTOMER_USER_MANAGER &&
          group !== Groups.CLIENT_ADMIN &&
          group !== Groups.ASSET_MANAGER &&
          group !== Groups.ASSET_VIEWER &&
          group !== Groups.CLIENT_ADMIN &&
          group !== Groups.PRODUCT_ADMIN
        );
      });
    } else {
      return false;
    }
  }

  /**
   * Check if current userManager has Users Global Admin group.
   * @returns {boolean}
   */
  public isUsersManagerAdmin(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return group === Groups.USER_ADMIN;
      });
    } else {
      return false;
    }
  }

  /**
   * Check if current user has Support User group.
   * @returns {boolean}
   */
  public isSupportUserGroup(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return group === Groups.SUPPORT_USER;
      });
    } else {
      return false;
    }
  }

  /**
   * Check if current user has Enterprise Service Provider User Manager Group.
   * @returns {boolean}
   */
  public isEnterpriseSPUserManager(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => {
        return group === Groups.ENTERPRISE_SP_USER_MANAGER;
      });
    } else {
      return false;
    }
  }

  /**
   * Check if current user has Users Global Admin group.
   * @returns {boolean}
   */
  public isClientsAdmin(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => group === Groups.CLIENT_ADMIN);
    } else {
      return false;
    }
  }

  /**
   * Given a group determine if it's a known valid group in the Admin Portal app.
   */
  public isAnyValidGroup(group): boolean {
    return (
      group === Groups.ASSET_ADMINISTRATOR ||
      group === Groups.ASSET_VIEWER ||
      group === Groups.ASSET_MANAGER ||
      group === Groups.USER_ADMIN ||
      group === Groups.CLIENT_ADMIN ||
      group === Groups.ENTERPRISE_SP_USER_MANAGER ||
      group === Groups.SPM_USER_MANAGER ||
      group === Groups.CUSTOMER_USER_MANAGER
    );
  }

  /**
   * Check if user is on access group.
   * @returns {boolean}
   */
  public isUserOnAccessGroup(): boolean {
    const profile = this.getLocalStorageProfile();
    if (!profile || !profile[GROUP_METADATA]) {
      return false;
    }
    const groups = profile[GROUP_METADATA];
    return groups.find(group => {
      return group === Groups.ADMIN_PORTAL;
    });
  }

  /**
   * Returns current user groups.
   * @returns {boolean}
   */
  private getUserGroups(): any {
    const profile = this.getLocalStorageProfile();
    if (!profile || !profile[GROUP_METADATA]) {
      return null;
    }
    const groups = profile[GROUP_METADATA];
    return groups;
  }

  /**
   * Determines which should be the user home page.
   */
  getUserHomePage(): string {
    if (this.isUsersAdmin()) {
      return '/console/user';
    } else if (this.isUsersManagerAdmin()) {
      return '/console/user';
    } else if (this.isEnterpriseSPUserManager()) {
      return '/console/user';
    } else if (this.isSPMUserManager()) {
      return '/console/spuser';
    } else if (this.isCustomerUserManager()) {
      return '/console/customerUser';
    } else if (this.isValidUser()) {
      return '/console/asset';
    } else {
      return '/console/unauthorized';
    }
  }
  public getUserInfo(): any {
    const userInfo = localStorage.getItem('userInfo');
    if (!userInfo || userInfo === 'undefined') {
      return null;
    }
    return JSON.parse(userInfo);
  }
  public setUserInfo(profile: any): void {
    const userInfo = {
      email: profile[USER_EMAIL],
      family_Name: profile.family_Name,
      given_name: profile.given_name,
      name: profile.name
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  }

  /**
   * Sets the local storage that the user manually logged out
   */

  public setLogoutLocalStorage(): void {
    localStorage.setItem('browser', 'true');
  }

  /**
   * Removes the local storage item that shows the user manually logged out
   */
  public removeLogoutLocalStorage(): void {
    localStorage.removeItem('browser');
  }

  /**
   * Gets the local storage item that the user manually logged out
   */
  public getLogoutLocalStorage(): string {
    return localStorage.getItem('browser');
  }

  public isFormsAdministrator(): boolean {
    let groups = this.getUserGroups();
    if (groups) {
      return groups.find(group => group === Groups.FORMS_ADMINISTRATOR);
    } else {
      return false;
    }
  }

  public getAccessToken(): string {
    if (this.getTokenDatafromStorage) {
      return this.getTokenDatafromStorage.accessToken;
    } else {
      this.config = this.msalService.getCurrentConfiguration();
      this.account = this.msalService.getAccount();
      if (this.config && this.account) {
        let authority = this.config.auth.authority;
        let clientId = this.config.auth.clientId;
        let homeAccountIdentifier = this.account.homeAccountIdentifier;
        let configData = JSON.stringify({
          authority: authority,
          clientId: clientId,
          homeAccountIdentifier: homeAccountIdentifier
        });
        let TokenObject = JSON.parse(localStorage.getItem(configData));
        if (TokenObject) {
          localStorage.setItem('token', TokenObject.accessToken);
          return TokenObject.accessToken;
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  }

  public getExpDate(): string {
    if (this.getTokenDatafromStorage) {
      return this.getTokenDatafromStorage.expiresIn;
    } else {
      this.config = this.msalService.getCurrentConfiguration();
      this.account = this.msalService.getAccount();
      if (this.config && this.account) {
        let authority = this.config.auth.authority;
        let clientId = this.config.auth.clientId;
        let homeAccountIdentifier = this.account.homeAccountIdentifier;
        let configData = JSON.stringify({
          authority: authority,
          clientId: clientId,
          homeAccountIdentifier: homeAccountIdentifier
        });
        let TokenObject = JSON.parse(localStorage.getItem(configData));
        if (TokenObject) {
          return TokenObject.expiresIn;
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  }

  public getIdToken(): string {
    if (this.getTokenDatafromStorage) {
      return this.getTokenDatafromStorage.idToken;
    } else {
      this.config = this.msalService.getCurrentConfiguration();
      this.account = this.msalService.getAccount();
      if (this.config && this.account) {
        let authority = this.config.auth.authority;
        let clientId = this.config.auth.clientId;
        let homeAccountIdentifier = this.account.homeAccountIdentifier;
        let configData = JSON.stringify({
          authority: authority,
          clientId: clientId,
          homeAccountIdentifier: homeAccountIdentifier
        });
        let TokenObject = JSON.parse(localStorage.getItem(configData));
        if (TokenObject) {
          return TokenObject.idToken;
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  }
}
